import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  onSnapshot,
  serverTimestamp, 
  query, 
  where, 
  orderBy, 
  limit, 
  setDoc, 
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import moment from "moment";
import { Dispatch } from "redux";
import { badWords } from "../../constants";
import { db } from "../../firebase";
import { ChatMessage, ChatRequest, ChatRequestStatus } from "../../interfaces";
import { deleteMessage, flagMessage, likeMessage, pinMessage, setChatData } from "../../redux/Chat/actions";

const EVENTS: any = {};

export const CHAT_SENDMESSAGE = "CHAT_SENDMESSAGE";
export const CHAT_SIGNINPASSWORD = "CHAT_SIGNINPASSWORD";
export const CHAT_CONVMOUNTED = "CHAT_CONVOMOUNTED";
export const CHAT_REFRESHCONVO = "CHAT_REFRESHCONVO";
export const CHAT_SENTMESSAGE = "CHAT_SENTMESSAGE";
export const CHAT_DELCONVO = "CHAT_DELCONVO";
export const CHAT_BANUSER = "CHAT_BANUSER";
export const CHAT_UNBANUSER = "CHAT_UNBANUSER";
export const CHAT_REGISTERUSER = "CHAT_REGISTERUSER";
export const CHAT_TOGGLECOMMENTLIKE = "CHAT_TOGGLECOMMENTLIKE";
export const CHAT_TOGGLECOMMENTFLAG = "CHAT_TOGGLECOMMENTFLAG";
export const CHAT_NEWMESSAGES = "CHAT_NEWMESSAGES";
export const CHAT_UNMOUNT = "CHAT_UNMOUNT";
export const CHAT_PINCONVO = "CHAT_PINCONVO";

export interface Message {
	id: string;
	chatId: string;
	userName: string;
	uid: string;
	message: string;
	photoURL: string;
	email: string;
	isHost: boolean;
	likesCount: number;
	likes: string[];
	timestamp: number | Timestamp;
	updatedAt: Timestamp;
	isDeleted: boolean;
}

let chatSnapshot: { [chatId: string]: () => void } = {};

let localDispatch: Dispatch<any> | null = null;

export const emit = (eventName: string, data: any, chatId: string) => {
	if (!EVENTS[chatId]) {
		return;
	}
	if (!EVENTS[chatId][eventName]) {
		return;
	}
	return new Promise<void>((resolve, reject) => {
		EVENTS[chatId][eventName].map(async (handler: any) => {
			await handler(data);
			resolve();
		});
	});
};

export const subscribe = (eventName: string, listener: any, chatId: string) => {
	if (EVENTS[chatId]) {
		if (EVENTS[chatId][eventName]) {
			EVENTS[chatId][eventName].push(listener);
		} else {
			EVENTS[chatId][eventName] = [listener];
		}
	} else {
		EVENTS[chatId] = { [eventName]: [listener] };
	}
};

export const resubscribe = (eventName: string, listener: any, chatId: string) => {
	if (EVENTS[chatId]) {
		if (EVENTS[chatId][eventName]) {
			EVENTS[chatId][eventName] = [listener];
		} else {
			EVENTS[chatId][eventName] = [listener];
		}
	} else {
		EVENTS[chatId] = { [eventName]: [listener] };
	}
};

const hasScriptTag = (message: string) => {
	const scriptTagRegex = /<[sS][cC][rR][iI][pP][tT][\s\S]*?>[\s\S]*?(?:<\/[sS][cC][rR][iI][pP][tT]>)?/;
	if (message.match(scriptTagRegex)) {
		return true;
	}
	return false;
};

const hasIFrame = (message: string) => {
	const iFrameTagRegex = /<[iI][fF][rR][aA][mM][eE][\s\S]*?>[\s\S]*?(?:<\/[iI][fF][rR][aA][mM][eE]>)?/;
	if (message.match(iFrameTagRegex)) {
		return true;
	}
	return false;
};

const createTextLinks = (message: string) => {
	return (message || "").replace(/([^\S]|^)(((https?\:\/\/)|(www\.))(\S+))/gi, function(match, space, url: string) {
		var hyperlink = url.replace(/([.,\/#!$%\^&\*;:{}=\-_`~()\]\[])+$/g, "");
		if (!hyperlink.match("^https?://")) {
			hyperlink = "http://" + hyperlink;
		}
		return space + '<a href="' + hyperlink + '" target="_blank">' + url + "</a>";
	});
};

const removeTextLinks = (message: string) => {
	return (message || "").replace(/([^\S]|^)(((https?\:\/\/)|(www\.))(\S+))/gi, function(match, space, url) {
		return "";
	});
};

const profanityFilter = (message: string) => {
	message = message || "";
	const regexp = new RegExp(`\\b(${badWords.join("|")})\\b`, "gi");
	return message.replace(regexp, function(str) {
		let replace_str = "";
		for (var i = 0; i < str.length; i++) {
			replace_str = replace_str + "*";
		}
		return replace_str;
	});
};

interface IMessagePayload extends Message {
	useChatProcessor: boolean;
	request: ChatRequest;
	contentId: string;
}

const sendMessage = async (messagePayload: IMessagePayload) => {
	const {
		chatId,
		userName,
		uid,
		message: incomingMessage,
		photoURL,
		email,
		isHost,
		useChatProcessor,
		request,
		contentId,
	} = messagePayload;

	if (hasScriptTag(incomingMessage) || hasIFrame(incomingMessage)) {
		emit(CHAT_SENTMESSAGE, false, chatId);
	} else {
		let message = incomingMessage.replace(/(<([^>]+)>)/gi, "");
		if (message.match(/([^\S]|^)(((https?\:\/\/)|(www\.))(\S+))/gi)) {
			if (isHost) {
				message = createTextLinks(message).replace(/(?:\r\n|\r|\n)/g, "<br>");
			} else {
				message = removeTextLinks(message).replace(/(?:\r\n|\r|\n)/g, "<br>");
			}
		} else {
			message = message.replace(/(?:\r\n|\r|\n)/g, "<br>");
		}
		const filteredMessage = profanityFilter(message);
		if (filteredMessage !== "") {
			const newMessage = {
				userName,
				uid,
				message: filteredMessage,
				timestamp: serverTimestamp(),
				updatedAt: serverTimestamp(),
				photoURL: photoURL ? `https://media.online.brushfire.com/profile_images/${uid}/profile.jpg` : null,
				//email, // Taking this out because we have it on the userAnalytics doc which isn't publicly readable
				isHost,
				flags: filteredMessage !== message ? ["profanity"] : [],
				isDeleted: false,
				likes: [],
				likesCount: 0,
				isPinned: false,
			};
			
			const chatCollectionRef = collection(
				db, 
				useChatProcessor ? "chat/processed-contents" : "chat/contents", 
				chatId
			);
			
			await addDoc(chatCollectionRef, newMessage)
				.then(docRef => {
					if (useChatProcessor && localDispatch) {
						localDispatch(
							setChatData({
								messages: {
									[docRef.id]: Object.assign({}, newMessage, {
										id: docRef.id,
										timestamp: undefined,
										updatedAt: Timestamp.fromDate(new Date()),
										likes: [],
										likesCount: 0,
										isPinned: false,
									}),
								},
								chatId,
								latestMessage: undefined,
								removeOldMessages: false,
							})
						);
					}
				})
				.catch(error => console.log(`error `, error));
				
			if (!!request) {
				const payload = Object.assign({}, request, { updatedAt: moment.utc().valueOf() });
				Object.keys(request.userData).forEach(key => {
					if (request.userData[key].status === ChatRequestStatus.directMessageEnded) {
						payload.userData[key].status = ChatRequestStatus.directMessageActive;
					}
				});
				payload.showRequest = true;
				const directDocRef = doc(db, "chat/direct", contentId, chatId);
				setDoc(directDocRef, payload, { merge: true });
			}
			emit(CHAT_SENTMESSAGE, true, chatId);
		}
	}
};

interface ISetupSnapshotListener {
	chatId: string;
	useChatProcessor: boolean;
}

interface ChatActivity {
	messages: ChatMessage[];
	createdAt: string;
	latestMessage: Timestamp;
}

export const clearOldSnapshot = (chatId: string) => {
	if (chatSnapshot[chatId]) {
		chatSnapshot[chatId]();
	}
};

const setupSnapshotListener = (messagePayload: ISetupSnapshotListener) => {
	const { chatId, useChatProcessor } = messagePayload;
	if (chatId) {
		if (useChatProcessor) {
			if (chatSnapshot[chatId]) {
				chatSnapshot[chatId]();
			}
			
			const activityDocRef = doc(db, "chat/processor", chatId, "activity");
			
			chatSnapshot[chatId] = onSnapshot(activityDocRef, async snapshot => {
				const data = snapshot.data() as ChatActivity;
				if (data) {
					const messages: { [key: string]: ChatMessage } = {};
					data.messages.forEach(message => {
						messages[message.id] = message;
					});
					emit(CHAT_NEWMESSAGES, Object.values(messages), chatId);
					if (localDispatch) {
						localDispatch(
							setChatData({
								latestMessage: data.latestMessage,
								chatId,
								messages,
								removeOldMessages: true,
							})
						);
					}
				}
			});
		} else {
			if (chatSnapshot[chatId]) {
				chatSnapshot[chatId]();
			}
			
			const chatCollectionRef = collection(db, "chat/contents", chatId);
			const q = query(
				chatCollectionRef,
				orderBy("timestamp", "desc"),
				limit(100)
			);
			
			chatSnapshot[chatId] = onSnapshot(q, async snapshot => {
				let convo: { id: string; timestamp: any }[] = [];
				// last is first
				snapshot.docs.forEach(doc => {
					//@ts-ignore Timestamp is there, I promise
					const data: { id: string; timestamp: any } = { id: doc.id, ...doc.data() };
					if (data.timestamp) {
						if (data.timestamp.seconds) {
							data.timestamp = parseInt(
								`${data.timestamp.seconds}${data.timestamp.seconds / 10000000}`
							);
						}
						convo.unshift(data);
					}
				});
				convo = convo.sort((el1, el2) => {
					return el1.timestamp - el2.timestamp;
				});
				emit(CHAT_REFRESHCONVO, { chatId, convo }, chatId);
				emit(CHAT_NEWMESSAGES, convo, chatId);
			});
		}
	}
};

const unmountListener = (params: any) => {
	if (chatSnapshot[params.chatId]) {
		chatSnapshot[params.chatId]();
	}
};

interface DelConvoParams {
	id: string;
	chatId: string;
	useChatProcessor: boolean;
	convo: ChatMessage;
}

const delConvo = async (params: DelConvoParams) => {
	const { id, chatId, useChatProcessor, convo } = params;
	const matchingPinnedChatId = convo.dupePinnedChatId ?? convo.originalId;
	
	const chatDocRef = doc(
		db, 
		useChatProcessor ? "chat/processed-contents" : "chat/contents", 
		chatId, 
		id
	);
	
	await updateDoc(chatDocRef, { 
		isDeleted: true, 
		updatedAt: serverTimestamp() 
	})
		.then(() => {
			if (useChatProcessor && localDispatch) {
				localDispatch(deleteMessage({ messageId: id }));
			}
		});
		
	if (matchingPinnedChatId) {
		const pinnedChatDocRef = doc(
			db, 
			useChatProcessor ? "chat/processed-contents" : "chat/contents", 
			chatId, 
			matchingPinnedChatId
		);
		
		await updateDoc(pinnedChatDocRef, {
			isPinned: false,
			isDeleted: true,
			updatedAt: serverTimestamp(),
		})
			.then(() => {
				if (useChatProcessor && localDispatch) {
					localDispatch(deleteMessage({ messageId: matchingPinnedChatId ?? "" }));
				}
			});
	}
};

export interface PinConvoParams {
	id: string;
	chatId: string;
	useChatProcessor: boolean;
	pin: boolean;
	convo: ChatMessage;
}

const pinConvo = async (params: PinConvoParams) => {
	const { id, chatId, useChatProcessor, pin, convo } = params;
	const chatCollectionRef = collection(
		db, 
		useChatProcessor ? "chat/processed-contents" : "chat/contents", 
		chatId
	);
	
	let dupeChatId = "";
	if (useChatProcessor && localDispatch) {
		localDispatch(pinMessage({ messageId: id }));
	}
	
	if (pin) {
		await addDoc(chatCollectionRef, {
			...convo,
			...{ originalId: id, isPinned: true, updatedAt: serverTimestamp() },
		})
			.then(async docRef => {
				dupeChatId = docRef.id;
				if (useChatProcessor && localDispatch) {
					localDispatch(pinMessage({ messageId: id, newDupeChatId: docRef.id }));
				}
				
				const origDocRef = doc(db, 
					useChatProcessor ? "chat/processed-contents" : "chat/contents", 
					chatId, 
					id
				);
				
				await updateDoc(origDocRef, { dupePinnedChatId: docRef.id });
			});
	}

	const q = query(chatCollectionRef, where("isPinned", "==", true));
	const oldPinnedChats = await getDocs(q);
	
	oldPinnedChats.forEach(async oldPinnedChat => {
		if (oldPinnedChat.id != dupeChatId) {
			const oldPinnedDocRef = doc(
				db, 
				useChatProcessor ? "chat/processed-contents" : "chat/contents", 
				chatId, 
				oldPinnedChat.id
			);
			
			await setDoc(oldPinnedDocRef, {
				isPinned: false,
				isDeleted: true,
				updatedAt: serverTimestamp(),
			}, { merge: true });
		}
		
		if (oldPinnedChat.data().dupePinnedChatId && !pin) {
			const dupeDocRef = doc(
				db, 
				useChatProcessor ? "chat/processed-contents" : "chat/contents", 
				chatId, 
				oldPinnedChat.data().dupePinnedChatId
			);
			
			await setDoc(dupeDocRef, {
				dupePinnedChatId: null,
				updatedAt: serverTimestamp(),
			}, { merge: true });
		}
	});
};

interface BanParams {
	chatId: string;
	uid: string;
	userName: string;
}

const banUser = async (params: BanParams) => {
	const { uid, userName, chatId } = params;
	const bannedDocRef = doc(db, "chat/banned", chatId, uid);

	const pinnedChatsQuery = query(
		collection(db, "chat/processed-contents", chatId),
		where("isPinned", "==", true)
	);
	
	const oldPinnedChats = await getDocs(pinnedChatsQuery);
	
	oldPinnedChats.forEach(async oldPinnedChat => {
		if ((oldPinnedChat.data() as ChatMessage).uid === uid) {
			const pinnedDocRef = doc(db, "chat/processed-contents", chatId, oldPinnedChat.id);
			await setDoc(pinnedDocRef, 
				{ 
					isPinned: false, 
					updatedAt: serverTimestamp() 
				}, 
				{ merge: true }
			);
		}
	});

	return setDoc(bannedDocRef, { userName });
};

const unbanUser = async (params: BanParams) => {
	const { uid, chatId } = params;
	const bannedDocRef = doc(db, "chat/banned", chatId, uid);
	return deleteDoc(bannedDocRef);
};

interface ToggleLikeParams {
	action: "like" | "unlike";
	chatId: string;
	id: string;
	uid: string;
	useChatProcessor: boolean;
	convo: ChatMessage;
}

const toggleCommentLike = async (params: ToggleLikeParams) => {
	const { id, chatId, uid, action, useChatProcessor, convo } = params;
	const matchingPinnedChatId = convo.dupePinnedChatId ?? convo.originalId;
	const commentDocRef = doc(
		db, 
		useChatProcessor ? "chat/processed-contents" : "chat/contents", 
		chatId, 
		id
	);

	const commentSnapshot = await getDoc(commentDocRef);
	const comment = commentSnapshot.exists() ? commentSnapshot.data() : null;

	if (!comment) return;

	let likesCount = comment.likesCount ? comment.likesCount : 0;
	let likes = comment.likes && comment.likes.length > 0 ? comment.likes : [];

	switch (action) {
		case "like":
			likesCount++;
			likes.push(uid);
			break;
		case "unlike":
			likesCount--;
			likes = likes.filter((likeUID: string) => likeUID !== uid);
			break;
		default:
			break;
	}

	await updateDoc(commentDocRef, { 
		likesCount, 
		likes, 
		updatedAt: serverTimestamp() 
	})
		.then(() => {
			if (useChatProcessor && localDispatch) {
				localDispatch(likeMessage({ messageId: id, uid }));
			}
		})
		.catch(error => console.log("error updating comment", error));

	if (matchingPinnedChatId) {
		const pinnedDocRef = doc(
			db, 
			useChatProcessor ? "chat/processed-contents" : "chat/contents", 
			chatId, 
			matchingPinnedChatId
		);
		
		await updateDoc(pinnedDocRef, {
			likesCount,
			likes,
			updatedAt: serverTimestamp(),
		})
			.then(() => {
				if (useChatProcessor && localDispatch) {
					localDispatch(likeMessage({ messageId: matchingPinnedChatId ?? "", uid }));
				}
			})
			.catch(error => console.log("error updating comment", error));
	}
};

interface ToggleFlagParams {
	action: "flag" | "unflag";
	chatId: string;
	id: string;
	uid: string;
	useChatProcessor: boolean;
	convo: ChatMessage;
}

const toggleCommentFlag = async (params: ToggleFlagParams) => {
	const { id, chatId, uid, action, useChatProcessor, convo } = params;
	const matchingPinnedChatId = convo.dupePinnedChatId ?? convo.originalId;
	const commentDocRef = doc(
		db, 
		useChatProcessor ? "chat/processed-contents" : "chat/contents", 
		chatId, 
		id
	);

	const commentSnapshot = await getDoc(commentDocRef);
	const comment = commentSnapshot.exists() ? commentSnapshot.data() : null;

	if (!comment) return;
	
	let flagsCount = comment.flagsCount ? comment.flagsCount : 0;
	let flags = comment.flags && comment.flags.length > 0 ? comment.flags : [];

	switch (action) {
		case "flag":
			flags.push(uid);
			break;
		case "unflag":
			flags = flags.filter((flagUID: string) => flagUID !== uid);
			break;
		default:
			break;
	}

	await updateDoc(commentDocRef, { 
		flagsCount, 
		flags, 
		updatedAt: serverTimestamp() 
	})
		.then(() => {
			if (useChatProcessor && localDispatch) {
				localDispatch(flagMessage({ messageId: id, uid }));
			}
		})
		.catch(error => console.log("error updating comment", error));

	if (matchingPinnedChatId) {
		const pinnedDocRef = doc(
			db, 
			useChatProcessor ? "chat/processed-contents" : "chat/contents", 
			chatId, 
			matchingPinnedChatId
		);
		
		await updateDoc(pinnedDocRef, { 
			flagsCount, 
			flags, 
			updatedAt: serverTimestamp() 
		})
			.then(() => {
				if (useChatProcessor && localDispatch) {
					localDispatch(flagMessage({ messageId: matchingPinnedChatId ?? "", uid }));
				}
			})
			.catch(error => console.log("error updating comment", error));
	}
};

export const initChat = (chatId: string, handleNewMessageCount: Function, dispatch: Dispatch<any>) => {
	EVENTS[chatId] = {};
	localDispatch = dispatch;
	subscribe(CHAT_SENDMESSAGE, sendMessage, chatId);
	//subscribe(CHAT_SIGNINPASSWORD, signInPassword, chatId);
	subscribe(
		CHAT_CONVMOUNTED,
		(data: any) => {
			setupSnapshotListener(data);
		},
		chatId
	);

	subscribe(CHAT_UNMOUNT, unmountListener, chatId);
	subscribe(CHAT_DELCONVO, delConvo, chatId);
	subscribe(CHAT_PINCONVO, pinConvo, chatId);
	subscribe(CHAT_TOGGLECOMMENTLIKE, toggleCommentLike, chatId);
	subscribe(CHAT_TOGGLECOMMENTFLAG, toggleCommentFlag, chatId);
	subscribe(CHAT_BANUSER, banUser, chatId);
	subscribe(CHAT_UNBANUSER, unbanUser, chatId);
	subscribe(CHAT_NEWMESSAGES, handleNewMessageCount, chatId);
	//subscribe(CHAT_REGISTERUSER, registerUser);
};

export const resubscribeToMessageHandler = (chatId: string, handleNewMessageCount: Function) => {
	resubscribe(CHAT_NEWMESSAGES, handleNewMessageCount, chatId);
};
